import { useEffect, useState } from "react";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { adminFetch } from "./adminApi";

const STATUSES = [
  "scheduled", "searching", "assigned", "arriving", "arrived_pickup",
  "picked_up", "en_route", "arrived_dropoff", "delivered", "cancelled",
];

interface BookingRow {
  bookingId: number;
  bookingCode: string;
  status: string;
  senderOpID?: number;
  driverId?: number | null;
  driverFirstName?: string;
  pickup?: { address?: string };
  dropoff?: { address?: string };
  createdAt?: string;
}

interface BookingDetail {
  booking: BookingRow;
  events: Array<{ eventId: number; status: string; note?: string; actorType: string; createdAt: string }>;
  disputes: any[];
  paymentEvents: any[];
}

export default function AdminBookings({ onBack }: { onBack: () => void }) {
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState<BookingDetail | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
      const res = await adminFetch(`/api/admin/bookings${qs}`);
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message);
      setBookings(payload.bookings || []);
    } catch (e) {
      toast.error("Failed to load bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (bookingId: number) => {
    try {
      const res = await adminFetch(`/api/admin/bookings/${bookingId}`);
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message);
      setSelected(payload);
    } catch (e) {
      toast.error("Failed to load booking detail.");
    }
  };

  const handleAssign = async () => {
    if (!selected) return;
    const driverId = window.prompt("Driver ID to assign:");
    if (!driverId) return;
    const reason = window.prompt("Reason (required):");
    if (!reason) return;
    setBusy(true);
    try {
      const res = await adminFetch(`/api/admin/bookings/${selected.booking.bookingId}/assign`, {
        method: "POST",
        body: JSON.stringify({ driverId: Number(driverId), reason }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message || "Failed to assign driver.");
      toast.success("Driver assigned.");
      await Promise.all([load(), openDetail(selected.booking.bookingId)]);
    } catch (e: any) {
      toast.error(e?.message || "Failed to assign driver.");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!selected) return;
    const reason = window.prompt("Reason for cancelling this booking (required):");
    if (!reason) return;
    setBusy(true);
    try {
      const res = await adminFetch(`/api/admin/bookings/${selected.booking.bookingId}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message || "Failed to cancel booking.");
      toast.success("Booking cancelled.");
      await Promise.all([load(), openDetail(selected.booking.bookingId)]);
    } catch (e: any) {
      toast.error(e?.message || "Failed to cancel booking.");
    } finally {
      setBusy(false);
    }
  };

  const handleStatusOverride = async () => {
    if (!selected) return;
    const status = window.prompt(`New status (one of: ${STATUSES.join(", ")}):`);
    if (!status || !STATUSES.includes(status)) {
      if (status) toast.error("Invalid status.");
      return;
    }
    const reason = window.prompt("Reason (required):");
    if (!reason) return;
    setBusy(true);
    try {
      const res = await adminFetch(`/api/admin/bookings/${selected.booking.bookingId}/status`, {
        method: "POST",
        body: JSON.stringify({ status, reason }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message || "Failed to update status.");
      toast.success("Status updated.");
      await Promise.all([load(), openDetail(selected.booking.bookingId)]);
    } catch (e: any) {
      toast.error(e?.message || "Failed to update status.");
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    // A plain <a href> navigation can't carry the admin's bearer token, so
    // fetch the CSV via adminFetch and save the resulting blob instead.
    void (async () => {
      const res = await adminFetch(`/api/admin/exports/bookings.csv${statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : ""}`);
      if (!res.ok) { toast.error("Export failed."); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "bookings.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    })();
  };

  return (
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button onClick={selected ? () => setSelected(null) : onBack} className="p-2 rounded-lg bg-[#0c1428] hover:bg-[#0f1e3d] text-[#a0aec0]"><ArrowLeft size={16} /></button>
          <h1 className="text-white text-lg sm:text-xl font-semibold">{selected ? `Booking ${selected.booking.bookingCode}` : "Bookings"}</h1>
        </div>
        {!selected && (
          <div className="flex items-center gap-2">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-[#0a1020] border border-white/[0.08] text-white rounded-lg px-3 py-2 text-sm">
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={exportCsv} className="px-3 py-2 bg-[#0c1428] hover:bg-[#0f1e3d] border border-white/[0.07] rounded-lg text-[#a0aec0] text-sm">Export CSV</button>
            <button onClick={() => void load()} className="p-2 bg-[#0c1428] hover:bg-[#0f1e3d] border border-white/[0.07] rounded-lg text-[#a0aec0]"><RefreshCw size={16} /></button>
          </div>
        )}
      </div>

      {!selected ? (
        loading ? (
          <div className="text-center text-[#6b7fa3] text-sm py-14">Loading bookings…</div>
        ) : (
          <div className="bg-[#0c1428] border border-white/[0.07] rounded-2xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#6b7fa3] border-b border-white/[0.06]">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Pickup</th>
                  <th className="px-4 py-3">Dropoff</th>
                  <th className="px-4 py-3">Driver</th>
                  <th className="px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.bookingId} onClick={() => void openDetail(b.bookingId)} className="border-b border-white/[0.04] text-[#e2e8f0] hover:bg-[#0f1e3d] cursor-pointer">
                    <td className="px-4 py-3 font-mono text-xs">{b.bookingCode}</td>
                    <td className="px-4 py-3">{b.status}</td>
                    <td className="px-4 py-3 max-w-[220px] truncate">{b.pickup?.address}</td>
                    <td className="px-4 py-3 max-w-[220px] truncate">{b.dropoff?.address}</td>
                    <td className="px-4 py-3">{b.driverId ?? "—"}</td>
                    <td className="px-4 py-3 text-xs text-[#6b7fa3]">{b.createdAt ? new Date(b.createdAt).toLocaleString() : ""}</td>
                  </tr>
                ))}
                {bookings.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-[#6b7fa3]">No bookings found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="space-y-4">
          <div className="bg-[#0c1428] border border-white/[0.07] rounded-2xl p-5 text-sm text-[#e2e8f0] space-y-1">
            <div>Status: <span className="text-white font-medium">{selected.booking.status}</span></div>
            <div>Pickup: {selected.booking.pickup?.address}</div>
            <div>Dropoff: {selected.booking.dropoff?.address}</div>
            <div>Driver: {selected.booking.driverId ?? "Unassigned"}</div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button disabled={busy} onClick={handleAssign} className="px-3 py-2 bg-[#00c9a7]/10 hover:bg-[#00c9a7]/20 border border-[#00c9a7]/30 rounded-lg text-[#00c9a7] text-sm">Assign / reassign driver</button>
            <button disabled={busy} onClick={handleStatusOverride} className="px-3 py-2 bg-[#0a1020] hover:bg-[#0f1e3d] border border-white/[0.08] rounded-lg text-[#a0aec0] text-sm">Override status</button>
            <button disabled={busy} onClick={handleCancel} className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">Cancel booking</button>
          </div>

          <div className="bg-[#0c1428] border border-white/[0.07] rounded-2xl p-5">
            <h2 className="text-white text-sm font-semibold mb-3">Event history</h2>
            <div className="space-y-2">
              {selected.events.map((e) => (
                <div key={e.eventId} className="text-xs text-[#a0aec0] flex justify-between border-b border-white/[0.04] pb-2">
                  <span>{e.status} <span className="text-[#6b7fa3]">({e.actorType})</span>{e.note ? ` — ${e.note}` : ""}</span>
                  <span className="text-[#6b7fa3]">{new Date(e.createdAt).toLocaleString()}</span>
                </div>
              ))}
              {selected.events.length === 0 && <div className="text-[#6b7fa3] text-xs">No events yet.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
